<template>
  <div class="dashboard">
    <div class="container">
      <h1 class="dashboard-title mb-4 section-title">Dashboard</h1>
      
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      
      <div v-else class="row g-4">
        <!-- Welcome Card -->
        <div class="col-12">
          <div class="card welcome-card">
            <div class="card-body p-4">
              <div class="row align-items-center">
                <div class="col-md-8">
                  <h2 class="card-title">Welcome, {{ accountInfo?.name || 'User' }}!</h2>
                  <p class="card-text mb-0">
                    <i class="bi bi-person-badge me-2 text-primary"></i>
                    Account Number: {{ accountInfo?.accountNumber || 'Not assigned' }}
                  </p>
                  <p class="card-text mb-0">
                    <i class="bi bi-gem me-2 text-primary"></i>
                    Plan: {{ accountInfo?.plan?.name }}
                  </p>
                  <p class="card-text">
                    <i class="bi bi-calendar-check me-2 text-primary"></i>
                    Billing Cycle: {{ formatDate(accountInfo?.billingCycleStart) }} - {{ formatDate(accountInfo?.billingCycleEnd) }}
                  </p>
                </div>
                <div class="col-md-4 text-md-end">
                  <router-link to="/account" class="btn btn-outline-primary">
                    <i class="bi bi-gear me-2"></i>Manage Account
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Usage Cards -->
        <div class="col-md-4">
          <div class="card usage-card h-100">
            <div class="card-body p-4">
              <div class="d-flex align-items-center mb-3">
                <i class="bi bi-wifi fs-4 text-primary me-2"></i>
                <h3 class="card-title h5 mb-0">Data Usage</h3>
              </div>
              <div class="progress-container mb-3">
                <div class="progress">
                  <div 
                    class="progress-bar" 
                    role="progressbar" 
                    :style="{ width: dataUsagePercentage + '%' }"
                    :class="{ 
                      'bg-success': dataUsagePercentage < 70,
                      'bg-warning': dataUsagePercentage >= 70 && dataUsagePercentage < 90,
                      'bg-danger': dataUsagePercentage >= 90 
                    }"
                  ></div>
                </div>
              </div>
              <p class="card-text fs-5 fw-bold">
                {{ formatData(usage?.data?.used) }} / 
                {{ isUnlimited(usage?.data?.limit) ? 'Unlimited' : formatData(usage?.data?.limit) }}
              </p>
              <p class="text-muted small">
                <i class="bi bi-arrow-repeat me-1"></i>
                Resets on {{ formatDate(accountInfo?.billingCycleEnd) }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="col-md-4">
          <div class="card usage-card h-100">
            <div class="card-body p-4">
              <div class="d-flex align-items-center mb-3">
                <i class="bi bi-telephone fs-4 text-primary me-2"></i>
                <h3 class="card-title h5 mb-0">Call Minutes</h3>
              </div>
              <div class="progress-container mb-3">
                <div class="progress">
                  <div 
                    class="progress-bar" 
                    role="progressbar" 
                    :style="{ width: minutesUsagePercentage + '%' }"
                    :class="{ 
                      'bg-success': minutesUsagePercentage < 70,
                      'bg-warning': minutesUsagePercentage >= 70 && minutesUsagePercentage < 90,
                      'bg-danger': minutesUsagePercentage >= 90 
                    }"
                  ></div>
                </div>
              </div>
              <p class="card-text fs-5 fw-bold">
                {{ usage?.minutes?.used }} / 
                {{ isUnlimited(usage?.minutes?.limit) ? 'Unlimited' : usage?.minutes?.limit }}
              </p>
              <p class="text-muted small">
                <i class="bi bi-arrow-repeat me-1"></i>
                Resets on {{ formatDate(accountInfo?.billingCycleEnd) }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="col-md-4">
          <div class="card usage-card h-100">
            <div class="card-body p-4">
              <div class="d-flex align-items-center mb-3">
                <i class="bi bi-chat-dots fs-4 text-primary me-2"></i>
                <h3 class="card-title h5 mb-0">SMS</h3>
              </div>
              <div class="progress-container mb-3">
                <div class="progress">
                  <div 
                    class="progress-bar" 
                    role="progressbar" 
                    :style="{ width: smsUsagePercentage + '%' }"
                    :class="{ 
                      'bg-success': smsUsagePercentage < 70,
                      'bg-warning': smsUsagePercentage >= 70 && smsUsagePercentage < 90,
                      'bg-danger': smsUsagePercentage >= 90 
                    }"
                  ></div>
                </div>
              </div>
              <p class="card-text fs-5 fw-bold">
                {{ usage?.sms?.used }} / 
                {{ isUnlimited(usage?.sms?.limit) ? 'Unlimited' : usage?.sms?.limit }}
              </p>
              <p class="text-muted small">
                <i class="bi bi-arrow-repeat me-1"></i>
                Resets on {{ formatDate(accountInfo?.billingCycleEnd) }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Recent Invoices -->
        <div class="col-12">
          <div class="card invoices-card">
            <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center p-4">
              <h3 class="h5 mb-0">
                <i class="bi bi-receipt-cutoff me-2 text-primary"></i>
                Recent Invoices
              </h3>
              <router-link to="/invoices" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-eye me-1"></i>View All
              </router-link>
            </div>
            <div class="card-body p-0">
              <invoice-list
                :invoices="invoices"
                :loading="loading"
                :error="error"
                :limit="5"
                :show-pagination="false"
                :default-sort-column="'date'"
                :default-sort-direction="'desc'"
              />
            </div>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import InvoiceList from '@/components/InvoiceList.vue'

export default {
  name: 'DashboardView',
  components: {
    InvoiceList
  },
  setup() {
    const store = useStore()
    
    // State
    const loading = ref(true)
    const error = ref(null)
    
    // Computed properties
    const accountInfo = computed(() => store.getters['account/accountInfo'])
    const usage = computed(() => store.getters['account/usage'])
    const invoices = computed(() => store.getters['invoices/allInvoices'])
    
    const dataUsagePercentage = computed(() => {
      if (!usage.value?.data) return 0
      if (isUnlimited(usage.value.data.limit)) return 50 // Show half full for unlimited
      return Math.min(100, (usage.value.data.used / usage.value.data.limit) * 100)
    })
    
    const minutesUsagePercentage = computed(() => {
      if (!usage.value?.minutes) return 0
      if (isUnlimited(usage.value.minutes.limit)) return 50 // Show half full for unlimited
      return Math.min(100, (usage.value.minutes.used / usage.value.minutes.limit) * 100)
    })
    
    const smsUsagePercentage = computed(() => {
      if (!usage.value?.sms) return 0
      if (isUnlimited(usage.value.sms.limit)) return 50 // Show half full for unlimited
      return Math.min(100, (usage.value.sms.used / usage.value.sms.limit) * 100)
    })
    
    // Methods
    const isUnlimited = (value) => {
      return value === -1 || value === 'unlimited' || value === null
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }
    
    const formatData = (bytes) => {
      if (!bytes || bytes === 0) return '0 GB'
      const gigabytes = bytes / (1024 * 1024 * 1024)
      return gigabytes.toFixed(2) + ' GB'
    }
    
    const formatCurrency = (amount, currency = 'EUR') => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount)
    }
    
    const downloadInvoice = (invoiceId) => {
      store.dispatch('invoices/downloadInvoice', invoiceId)
    }
    
    // Fetch data on component mount
    onMounted(async () => {
      try {
        loading.value = true
        await Promise.all([
          store.dispatch('account/fetchAccountInfo'),
          store.dispatch('account/fetchUsage'),
          store.dispatch('invoices/fetchInvoices')
        ])
      } catch (err) {
        error.value = 'Failed to load dashboard data. Please try again.'
        console.error(err)
      } finally {
        loading.value = false
      }
    })
    
    return {
      loading,
      error,
      accountInfo,
      usage,
      invoices,
      dataUsagePercentage,
      minutesUsagePercentage,
      smsUsagePercentage,
      isUnlimited,
      formatDate,
      formatData,
      formatCurrency,
      downloadInvoice
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 30px 0;
}

.dashboard-title {
  position: relative;
  margin-bottom: 30px;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 60px;
  height: 3px;
  background-color: var(--primary-color);
}

.welcome-card {
  border: none;
  border-radius: 12px;
  background-image: linear-gradient(to right, rgba(162, 77, 206, 0.1), rgba(162, 77, 206, 0.02));
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.usage-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.invoices-card, .support-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.progress-container {
  position: relative;
  width: 100%;
}

.progress {
  height: 12px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.progress-bar {
  transition: width 1s ease;
  border-radius: 6px;
}

/* Dark mode adjustments */
[data-theme="dark"] .welcome-card {
  background-image: linear-gradient(to right, rgba(176, 90, 215, 0.2), rgba(176, 90, 215, 0.05));
}

[data-theme="dark"] .progress {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>